import { RuleChip } from "@/components/ui/rule-chip";
import { fmtNum, fmtRubShort } from "@/lib/format";
import { formatMessage, reconciliationResultMessages } from "@/services/messages";
import type { RuleViolationResponse } from "@efes/shared";

/**
 * Δ-pills — компактная полоса agregated-расхождений сверху matrix-карточки (фича 024 ④).
 *
 * Из массива violations подсасывает данные `comparison` каждого правила и
 * рендерит pill-формат: «Δ объём +325 кг +13.5% · RC-P5».
 *
 * Чисто из view-data: не делает API-вызовов, не хранит state.
 */
export interface DiscrepancyDeltaPillsProps {
	violations: ReadonlyArray<RuleViolationResponse>;
}

export function DiscrepancyDeltaPills({ violations }: DiscrepancyDeltaPillsProps) {
	const pills: { ruleId: string; node: React.ReactNode }[] = [];
	for (const v of violations) {
		const node = renderPill(v);
		if (node) pills.push({ ruleId: v.ruleId, node });
	}
	if (pills.length === 0) return null;
	return (
		<div className="mx-4 mb-3 flex flex-wrap items-center gap-2">
			{pills.map((p, i) => (
				<span key={`${p.ruleId}-${i}`}>{p.node}</span>
			))}
		</div>
	);
}

function renderPill(v: RuleViolationResponse): React.ReactNode | null {
	const messages = reconciliationResultMessages.redesign;
	const tone = v.severity === "warning" ? "warning" : "error";
	const c = v.trace?.comparison;
	const diff = c?.diff;
	const diffPercent = c?.diffPercent;
	const unit = c?.unit;

	const valueClass = tone === "warning" ? "text-yellow-800" : "text-red-700";
	const pctClass = tone === "warning" ? "text-yellow-700" : "text-red-600";

	if (v.ruleId === "RC-P5" && typeof diff === "number") {
		return (
			<Pill tone={tone}>
				<span className="text-slate-500">{messages.delta_volume_label}</span>
				<span className={`font-mono font-semibold ${valueClass}`}>
					{signed(diff)} {unit ?? ""}
				</span>
				{typeof diffPercent === "number" ? (
					<span className={`font-mono text-[11px] ${pctClass}`}>{signedPct(diffPercent)}</span>
				) : null}
				<span className="text-slate-300">{messages.delta_pill_separator}</span>
				<RuleChip>{v.ruleId}</RuleChip>
			</Pill>
		);
	}
	if (v.ruleId === "RC-P7" && typeof diff === "number") {
		return (
			<Pill tone={tone}>
				<span className="text-slate-500">{messages.delta_cost_label}</span>
				<span className={`font-mono font-semibold ${valueClass}`}>
					{diff >= 0 ? "+" : "−"}
					{fmtRubShort(Math.abs(diff))}
				</span>
				{typeof diffPercent === "number" ? (
					<span className={`font-mono text-[11px] ${pctClass}`}>{signedPct(diffPercent)}</span>
				) : null}
				<span className="text-slate-300">{messages.delta_pill_separator}</span>
				<RuleChip>{v.ruleId}</RuleChip>
			</Pill>
		);
	}
	if (
		v.ruleId === "RC-P6" &&
		typeof c?.expected === "number" &&
		typeof c?.actual === "number"
	) {
		// RC-P6 — per-position rate mismatch. Σ-суммы могут случайно совпасть,
		// поэтому matrix-Δ-row покажет 0; этот pill — главный сигнал расхождения.
		return (
			<Pill tone={tone}>
				<span className="text-slate-500">{messages.delta_rate_label}</span>
				<span className={`font-mono font-semibold ${valueClass}`}>
					{/* Sprint 6 / C9.1: используем fmtRubShort для краткости (5 000 ₽
					    вместо 5000 ₽), формат «{expected} ₽ → {actual} ₽» как в
					    остальных delta-pill'ах. */}
					{formatMessage(messages.delta_rate_template, {
						expected: fmtRubShort(c.expected),
						actual: fmtRubShort(c.actual),
					})}
				</span>
				{typeof diffPercent === "number" ? (
					<span className={`font-mono text-[11px] ${pctClass}`}>{signedPct(diffPercent)}</span>
				) : null}
				<span className="text-slate-300">{messages.delta_pill_separator}</span>
				<RuleChip>{v.ruleId}</RuleChip>
			</Pill>
		);
	}
	if (v.ruleId === "RC-P3") {
		const similarity =
			typeof v.metadata?.similarity === "number" ? Math.round(v.metadata.similarity * 100) : null;
		return (
			<Pill tone="warning">
				<span className="text-slate-500">{messages.delta_name_label}</span>
				{similarity !== null ? (
					<span className="font-mono font-semibold text-yellow-800">
						{formatMessage(messages.delta_name_similarity_template, { percent: similarity })}
					</span>
				) : null}
				<span className="text-slate-300">{messages.delta_pill_separator}</span>
				<RuleChip>{v.ruleId}</RuleChip>
			</Pill>
		);
	}
	return null;
}

function signed(n: number): string {
	return `${n >= 0 ? "+" : "−"}${fmtNum(Math.abs(n))}`;
}

function signedPct(p: number): string {
	const sign = p >= 0 ? "+" : "−";
	return `${sign}${Math.abs(p).toFixed(1)}%`;
}

function Pill({ tone, children }: { tone: "error" | "warning"; children: React.ReactNode }) {
	const cls = tone === "error" ? "bg-red-50 ring-red-200" : "bg-yellow-50 ring-yellow-200";
	return (
		<span
			className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs ring-1 ring-inset ${cls}`}
		>
			{children}
		</span>
	);
}
